// @flow
/* eslint-disable arrow-body-style */
import React, { forwardRef, useCallback } from 'react';
import classNames from 'classnames';
import useDebounce from 'javascripts/hooks/useDebounce';
import Label from 'components/atoms/label/react/Label';
import Input from 'components/atoms/input/react/Input';
import Button from 'components/atoms/button/Button';
import { t } from 'javascripts/utils/withTranslation';
import { useCombobox } from 'downshift';
import {
  searchAuthorities,
  searchAssociatedCommunities,
} from '../authority-search-util';
import type { Item, CommunitySuggestion } from '../types';

type AutocompleteProps = {
  fuse: any,
  maxScore: number,
  items: Item[],
  presetValue?: string,
  searchValue?: string,
  setSearchValue: (value: string) => void,
  handleClear: () => void,
  setItems: (items: Item[]) => void,
};

const AuthorityAutocomplete: React.AbstractComponent<
  AutocompleteProps,
  HTMLInputElement,
> = forwardRef<AutocompleteProps, HTMLInputElement>(
  (props: AutocompleteProps, searchInputRef) => {
    const {
      fuse,
      maxScore,
      items,
      presetValue,
      searchValue,
      setSearchValue,
      handleClear,
      setItems,
    } = props;
    const [autocompleteItems, setAutocompleteItems] = React.useState<
      CommunitySuggestion[],
    >([]);
    const onInputValueChange = useCallback(
      async (inputValue: string) => {
        if (inputValue.length < 2) {
          setAutocompleteItems([]);
          return;
        }

        try {
          const res = await searchAssociatedCommunities(items, inputValue);
          setAutocompleteItems(res);
        } catch (error) {
          console.error('Error fetching autocomplete suggestions:', error);
        }
      },
      [items],
    );
    const debouncedInputValueChange = useDebounce(onInputValueChange, 450);

    const {
      isOpen,
      getLabelProps,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
    } = useCombobox({
      initialInputValue: presetValue || '',
      items: autocompleteItems,
      itemToString: (item) => (item ? item.value : ''),
      onInputValueChange: ({ inputValue }) => {
        const nextValue = inputValue ?? '';
        setSearchValue(nextValue);
        debouncedInputValueChange(nextValue);
      },
      inputValue: searchValue,
      onSelectedItemChange: async ({ selectedItem: newSelectedItem }) => {
        if (newSelectedItem) {
          setSearchValue(newSelectedItem.value);
          const res = await searchAuthorities(items, newSelectedItem.value, {
            fuse,
            maxScore,
          });
          setItems(res);
        }
      },
    });

    /** Prevent Downshift's default 'Enter' behavior, when no item is highlighted. This is necessary to allow submitting the form via Enter when searching for something that is not in the suggestions. */
    const handleKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        if (highlightedIndex === -1) {
          // eslint-disable-next-line no-param-reassign
          event.nativeEvent.preventDownshiftDefault = true;
        }
      }
    };

    const handleFocus = (e: any) => {
      e.target.select();
    };

    const getDropdownItem = useCallback((item: CommunitySuggestion) => {
      const before = item.value.slice(0, item.matchIndex);
      const match = item.value.slice(
        item.matchIndex,
        item.matchIndex + item.matchLength,
      );
      const after = item.value.slice(item.matchIndex + item.matchLength);

      return (
        <span className="auth-autocomplete__title">
          {before}
          <mark className="text-highlight" data-markjs="true">
            {match}
          </mark>
          {after}
        </span>
      );
    }, []);

    return (
      <div className="auth-autocomplete form-group">
        <Label {...getLabelProps()}>{t('Zuständige Behörde finden')}</Label>
        <div className="search__wrap">
          <Input
            {...getInputProps({
              placeholder: t('Ort eingeben'),
              name: 'authority-search-input',
              onFocus: handleFocus,
              onKeyDown: handleKeyDown,
              ref: searchInputRef,
            })}
            classNames="input--large"
            type="search"
          />
          {searchValue && searchValue.length > 0 && (
            <Button
              icon="close"
              style="icon"
              onClick={handleClear}
              className="button--large search__close"
              ariaLabel={t('Sucheingabe löschen')}
            />
          )}
        </div>
        <div
          className={classNames(
            'search__dropdown',
            'auth-autocomplete__dropdown',
            isOpen &&
              autocompleteItems.length > 0 &&
              'dropdown-menu search__dropdown--open',
          )}
        >
          <span
            className="u-hidden-visually"
            id={`${getMenuProps().id}-info`}
            aria-live="polite"
          >
            {isOpen &&
              `Es sind ${autocompleteItems.length} Vorschläge für Ihre Suche vorhanden. Navigieren Sie mit den Pfeiltasten. Die Auswahl kann mit der Entertaste bestätigt werden. Die unten stehenden Suchergebnisse werden entsprechend sofort gefiltert.`}
          </span>
          <ul
            className="dropdown-menu__list"
            {...getMenuProps()}
            aria-describedby={`${getMenuProps().id}-info`}
          >
            {isOpen &&
              autocompleteItems.length > 0 &&
              autocompleteItems.map((item: CommunitySuggestion, index) => {
                const listItemClassName = classNames(
                  'dropdown-menu__list-item',
                  highlightedIndex === index &&
                    'dropdown-menu__list-item--active',
                );
                return (
                  <li
                    className={listItemClassName}
                    key={`${item.value}${index}`}
                    {...getItemProps({ item, index })}
                  >
                    <span className="dropdown-menu__link">
                      <span className="dropdown-menu__text">
                        {getDropdownItem(item)}
                      </span>
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    );
  },
);

AuthorityAutocomplete.displayName = 'AuthorityAutocomplete';

export default AuthorityAutocomplete;
